import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { toast } from "react-toastify";

import {
  FaLayerGroup,
  FaQuestionCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileAlt,
} from "react-icons/fa";

import {
  useGetQuizBlueprintQuery,
  useGetBlueprintStatisticsQuery,
  useGenerateQuizQuestionsMutation,
} from "../../slices/quizApiSlice";

const GenerateQuestionPaperScreen = () => {
  const { quizId } = useParams();

  const navigate = useNavigate();

  const [generatedPaper, setGeneratedPaper] = useState(null);

  const {
    data: blueprintResponse,
    isLoading: loadingBlueprint,
    error: blueprintError,
  } = useGetQuizBlueprintQuery(quizId);

  const {
    data: statisticsResponse,
    isLoading: loadingStatistics,
    error: statisticsError,
  } = useGetBlueprintStatisticsQuery(quizId);

  const [generateQuizQuestions, { isLoading: generating }] =
    useGenerateQuizQuestionsMutation();

  const blueprintData = blueprintResponse?.data || {};

  const sections = blueprintData.sections || [];

  const totalQuestions = Number(
    blueprintData.totalQuestions ||
      sections.reduce(
        (sum, section) => sum + Number(section.numberOfQuestions || 0),
        0,
      ),
  );

  const canGenerate =
    sections.length > 0 &&
    totalQuestions > 0 &&
    (statisticsResponse?.data?.canGenerate ??
      statisticsResponse?.summary?.canGenerate ??
      false);

  const handleGenerate = async () => {
    try {
      const response = await generateQuizQuestions(quizId).unwrap();

      setGeneratedPaper(response?.data || response);

      toast.success("Question paper generated successfully");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to generate question paper");
    }
  };

  if (loadingBlueprint || loadingStatistics) {
    return (
      <div className="p-10 text-center">Loading generation details...</div>
    );
  }

  if (blueprintError) {
    return (
      <div className="p-10 text-red-600">
        Blueprint Error:{" "}
        {blueprintError?.data?.message || "Unable to load blueprint"}
      </div>
    );
  }

  if (statisticsError) {
    return (
      <div className="p-10 text-red-600">
        Statistics Error:{" "}
        {statisticsError?.data?.message || "Unable to load statistics"}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate(`/quizzes/${quizId}`)}
          className="mb-6 rounded-xl bg-white px-5 py-3 text-blue-600 shadow"
        >
          ← Back to Quiz
        </button>

        <div className="rounded-3xl bg-white/80 backdrop-blur shadow-xl p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="rounded-xl bg-green-100 p-4 text-green-600">
              <FaFileAlt className="text-3xl" />
            </div>

            <div>
              <h1 className="text-3xl font-bold">Generate Question Paper</h1>

              <p className="text-gray-600">
                Generate questions based on quiz blueprint
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <InfoCard
              icon={<FaLayerGroup />}
              title="Blueprint Sections"
              value={sections.length}
            />

            <InfoCard
              icon={<FaQuestionCircle />}
              title="Required Questions"
              value={totalQuestions}
            />

            <InfoCard
              icon={canGenerate ? <FaCheckCircle /> : <FaExclamationTriangle />}
              title="Availability"
              value={canGenerate ? "Ready" : "Insufficient"}
            />
          </div>

          {sections.length === 0 && (
            <div className="mt-8 rounded-xl bg-yellow-50 border border-yellow-200 p-5 text-yellow-700">
              <FaExclamationTriangle className="inline mr-2" />
              No blueprint sections found. Please create a blueprint first.
            </div>
          )}

          {!canGenerate && sections.length > 0 && (
            <div className="mt-8 rounded-xl bg-red-50 border border-red-200 p-5 text-red-700">
              <FaExclamationTriangle className="inline mr-2" />
              Not enough questions are available to generate this paper.
            </div>
          )}

          {sections.length > 0 && (
            <button
              disabled={generating || !canGenerate}
              onClick={handleGenerate}
              className="
              mt-8
              w-full
              rounded-xl
              bg-green-600
              py-4
              text-lg
              font-semibold
              text-white
              disabled:opacity-50
              "
            >
              {generating ? "Generating Paper..." : "Generate Question Paper"}
            </button>
          )}

          {generatedPaper && (
            <div className="mt-10 rounded-xl border bg-gray-50 p-6">
              <h2 className="text-xl font-bold mb-5">
                Generated Question Paper
              </h2>

              {generatedPaper.questions?.map((question, index) => (
                <div
                  key={question._id}
                  className="mb-4 rounded-lg bg-white border p-4"
                >
                  <p className="font-semibold">
                    {index + 1}. {question.question}
                  </p>

                  <p className="text-sm text-gray-500 mt-2">
                    Difficulty: {question.difficulty}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoCard = ({ icon, title, value }) => (
  <div className="rounded-xl bg-gray-50 border p-5">
    <div className="text-blue-600 text-xl">{icon}</div>

    <p className="mt-3 font-semibold">{title}</p>

    <p className="text-2xl font-bold">{value}</p>
  </div>
);

export default GenerateQuestionPaperScreen;
